import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { assetUrl, axiosForAPI } from '../helper_functions/utilities'
import Loading from './Loading'
import LadderError from '../errors/LadderError'
import { RiArrowLeftDoubleFill, RiArrowRightDoubleFill } from "react-icons/ri"
import LadderChampionIconWithFallback from '../shared/LadderChampionIcon'
import axios from 'axios'
import Seo from './Seo'
import { buttonFocusRingClass, neutralButtonOnZincSurfaceClass } from '../shared/buttonStyles'
import { useDeploymentCapabilities } from '../context/DeploymentCapabilitiesContext'


const Globe = lazy(() => import('react-globe.gl'))

const REGION_OPTIONS = [
    { value: 'na', label: 'NA' },
    { value: 'euw', label: 'EUW' },
    { value: 'eun', label: 'EUN' },
    { value: 'lan', label: 'LAN' },
    { value: 'las', label: 'LAS' },
    { value: 'br', label: 'BR' },
    { value: 'kr', label: 'KR' },
    { value: 'jp', label: 'JP' },
    { value: 'me', label: 'ME' },
    { value: 'oc', label: 'OC' },
    { value: 'tr', label: 'TR' },
    { value: 'ru', label: 'RU' },
    { value: 'sg', label: 'SG' },
    { value: 'tw', label: 'TW' },
    { value: 'vn', label: 'VN' },
]

const REGION_LEFT_COLUMN = ['na', 'jp']
const REGION_MIDDLE_TOP_ROW = ['euw', 'eun', 'lan', 'las']
const REGION_MIDDLE_BOTTOM_ROW = ['me', 'oc', 'tr', 'ru', 'sg']
const REGION_RIGHT_COLUMNS = [
    ['br', 'tw'],
    ['kr', 'vn'],
]

const REGION_GLOBE_COORDINATES = {
    na: { lat: 41.88, lng: -87.63 },
    euw: { lat: 52.37, lng: 4.9 },
    eun: { lat: 52.23, lng: 21.01 },
    lan: { lat: 19.43, lng: -99.13 },
    las: { lat: -34.6, lng: -58.38 },
    br: { lat: -23.55, lng: -46.63 },
    kr: { lat: 37.57, lng: 126.98 },
    jp: { lat: 35.68, lng: 139.69 },
    me: { lat: 25.2, lng: 55.27 },
    oc: { lat: -33.87, lng: 151.21 },
    tr: { lat: 41.01, lng: 28.97 },
    ru: { lat: 55.76, lng: 37.62 },
    sg: { lat: 1.35, lng: 103.82 },
    tw: { lat: 25.03, lng: 121.56 },
    vn: { lat: 10.82, lng: 106.63 },
}

const REGION_COUNTRY_CODES = {
    na: ['US', 'CA'],
    euw: ['GB', 'IE', 'FR', 'ES', 'PT', 'BE', 'NL', 'DE', 'CH', 'AT', 'IT'],
    eun: ['PL', 'CZ', 'SK', 'HU', 'RO', 'BG', 'GR', 'UA', 'LT', 'LV', 'EE', 'HR', 'SI', 'RS', 'BA', 'AL', 'MK', 'MD'],
    lan: ['MX', 'GT', 'SV', 'HN', 'NI', 'CR', 'PA'],
    las: ['AR', 'CL', 'UY', 'PY', 'BO', 'PE'],
    br: ['BR'],
    kr: ['KR'],
    jp: ['JP'],
    me: ['AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'JO', 'LB', 'EG'],
    oc: ['AU', 'NZ'],
    tr: ['TR'],
    ru: ['RU'],
    sg: ['SG', 'MY', 'ID', 'TH', 'PH'],
    tw: ['TW'],
    vn: ['VN'],
}

const LADDER_TYPE_OPTIONS = [
    { value: 'solo', label: 'SOLO/DUO QUEUE RANKED' },
    { value: 'flex', label: 'FLEX QUEUE RANKED' },
]
const LADDER_TYPE_META_LABELS = {
    solo: 'Solo/Duo Queue Ranked',
    flex: 'Flex Queue Ranked',
}

const DISPLAY_TO_REGION = {
    na: 'americas',
    br: 'americas',
    lan: 'americas',
    las: 'americas',
    euw: 'europe',
    eun: 'europe',
    tr: 'europe',
    ru: 'europe',
    me: 'europe',
    kr: 'asia',
    jp: 'asia',
    oc: 'asia',
    sg: 'asia',
    tw: 'asia',
    vn: 'asia',
}

const DISPLAY_TO_PLATFORM = {
    na: 'na1',
    br: 'br1',
    lan: 'la1',
    las: 'la2',
    euw: 'euw1',
    eun: 'eun1',
    tr: 'tr1',
    ru: 'ru',
    kr: 'kr',
    me: 'me1',
    jp: 'jp1',
    oc: 'oc1',
    sg: 'sg2',
    tw: 'tw2',
    vn: 'vn2',
}

const TIER_ICON_MAP = {
    CHALLENGER: 'Rank=Challenger',
    GRANDMASTER: 'Rank=Grandmaster',
    MASTER: 'Rank=Master',
    DIAMOND: 'Rank=Diamond',
    EMERALD: 'Rank=Emerald',
    PLATINUM: 'Rank=Platinum',
    GOLD: 'Rank=Gold',
    SILVER: 'Rank=Silver',
    BRONZE: 'Rank=Bronze',
    IRON: 'Rank=Iron',
}

const TIER_LABEL_ABBREVIATIONS = {
    CHALLENGER: 'CHAL',
    GRANDMASTER: 'GM',
    MASTER: 'MSTR',
    DIAMOND: 'DIAM',
    EMERALD: 'EMER',
    PLATINUM: 'PLAT',
    GOLD: 'GOLD',
    SILVER: 'SILV',
    BRONZE: 'BRNZ',
    IRON: 'IRON',
}

const APEX_TIERS = new Set(['CHALLENGER', 'GRANDMASTER', 'MASTER'])
const LADDER_GRID_TEMPLATE = '40px minmax(84px, 0.84fr) 156px 144px 184px 60px'
const LADDER_GRID_TEMPLATE_COMPACT = '34px minmax(96px, 0.96fr) 104px 72px 126px 52px'
const ladderSelectedButtonClassName = '!border-cyan-700/70 !bg-cyan-950/55 !text-cyan-100 hover:!border-cyan-600/80 hover:!bg-cyan-950/70 hover:!text-cyan-50'

const filterPillInactiveClassName = `inline-flex h-8 items-center justify-center rounded-md px-3 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${buttonFocusRingClass} ${neutralButtonOnZincSurfaceClass}`
const filterPillActiveClassName = `inline-flex h-8 items-center justify-center rounded-md px-3 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${buttonFocusRingClass} ${ladderSelectedButtonClassName}`
const queueFilterButtonInactiveClassName = `inline-flex h-7 items-center justify-center rounded-md px-3 text-[10px] font-semibold uppercase tracking-[0.16em] transition-colors ${buttonFocusRingClass} ${neutralButtonOnZincSurfaceClass}`
const queueFilterButtonActiveClassName = `inline-flex h-7 items-center justify-center rounded-md px-3 text-[10px] font-semibold uppercase tracking-[0.16em] transition-colors ${buttonFocusRingClass} ${ladderSelectedButtonClassName}`
const paginationButtonInactiveClassName = `inline-flex h-8 min-w-8 items-center justify-center rounded-md px-3 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${buttonFocusRingClass} ${neutralButtonOnZincSurfaceClass}`
const paginationButtonActiveClassName = `inline-flex h-8 min-w-8 items-center justify-center rounded-md px-3 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${buttonFocusRingClass} ${ladderSelectedButtonClassName}`
const controlCardClassName = 'min-w-0 rounded-lg border border-[#33333a] bg-zinc-950 p-3 text-zinc-200 shadow-[0_12px_28px_-24px_rgba(0,0,0,0.9)]'

const LadderGlobe = ({ selectedRegion, compact = false }) => {
    const globeRef = useRef(null)
    const [countries, setCountries] = useState([])
    const selectedCoordinates = REGION_GLOBE_COORDINATES[selectedRegion] || REGION_GLOBE_COORDINATES.na
    const selectedCountryCodes = REGION_COUNTRY_CODES[selectedRegion] || REGION_COUNTRY_CODES.na
    const globeSize = compact ? 104 : 130

    useEffect(() => {
        let ignore = false

        fetch(`${process.env.PUBLIC_URL}/data/world-countries.geojson`)
            .then((response) => response.json())
            .then((data) => {
                if (!ignore) {
                    setCountries(data?.features || [])
                }
            })
            .catch(() => {
                if (!ignore) {
                    setCountries([])
                }
            })

        return () => {
            ignore = true
        }
    }, [])

    useEffect(() => {
        let retryTimeout
        let rotationTimeout
        let cancelled = false

        const syncGlobe = () => {
            const globe = globeRef.current

            if (!globe) {
                retryTimeout = window.setTimeout(syncGlobe, 60)
                return
            }

            const controls = globe.controls?.()
            if (controls) {
                controls.enablePan = false
                controls.enableZoom = false
                controls.autoRotate = false
            }

            globe.pointOfView(
                {
                    lat: selectedCoordinates.lat,
                    lng: selectedCoordinates.lng,
                    altitude: 1.65,
                },
                1200,
            )

            rotationTimeout = window.setTimeout(() => {
                if (cancelled || !globeRef.current) {
                    return
                }

                const nextControls = globeRef.current.controls?.()
                if (nextControls) {
                    nextControls.autoRotate = true
                    nextControls.autoRotateSpeed = 0.175
                }
            }, 1250)
        }

        syncGlobe()

        return () => {
            cancelled = true
            window.clearTimeout(retryTimeout)
            window.clearTimeout(rotationTimeout)
        }
    }, [selectedCoordinates.lat, selectedCoordinates.lng, countries.length])

    return (
        <div className={`ladder-globe-card flex h-full items-center justify-center ${compact ? 'min-h-[108px]' : 'min-h-[152px]'}`}>
            <Suspense fallback={<div className={`${compact ? 'h-[104px] w-[104px]' : 'h-[132px] w-[132px]'} rounded-full bg-zinc-950/80 shadow-[inset_0_0_0_1px_rgba(63,63,70,0.55)]`} />}>
                <Globe
                    ref={globeRef}
                    width={globeSize}
                    height={globeSize}
                    backgroundColor="rgba(0,0,0,0)"
                    polygonsData={countries}
                    polygonAltitude={(feature) => (selectedCountryCodes.includes(feature?.properties?.ISO_A2) ? 0.02 : 0.01)}
                    polygonCapColor={(feature) => (selectedCountryCodes.includes(feature?.properties?.ISO_A2) ? 'rgba(56, 189, 248, 0.82)' : 'rgba(113, 113, 122, 0.72)')}
                    polygonSideColor={() => 'rgba(39, 39, 42, 0.2)'}
                    polygonStrokeColor={() => 'rgba(24, 24, 27, 0.55)'}
                    waitForGlobeReady={false}
                    showAtmosphere
                    atmosphereColor="#1d4ed8"
                    atmosphereAltitude={0.09}
                    enablePointerInteraction={false}
                    animateIn={false}
                />
            </Suspense>
        </div>
    )
}

const Ladder = ({ globallyUpdateDisplayedRegion }) => {
    const { isPlatformSupported } = useDeploymentCapabilities()
    const params = useParams()
    const location = useLocation()
    const navigate = useNavigate()

    const [rankedLadderDetails, setRankedLadderDetails] = useState({})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
    const [isCompactLadder, setIsCompactLadder] = useState(() => (
        typeof window !== 'undefined' ? window.innerWidth < 900 : false
    ))

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined
        }

        const mediaQuery = window.matchMedia('(max-width: 899px)')
        const syncCompactLadder = (event) => setIsCompactLadder(event.matches)

        setIsCompactLadder(mediaQuery.matches)

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', syncCompactLadder)
            return () => mediaQuery.removeEventListener('change', syncCompactLadder)
        }

        mediaQuery.addListener(syncCompactLadder)
        return () => mediaQuery.removeListener(syncCompactLadder)
    }, [])

    const getLeaderboard = useCallback(async () => {
        const controller = new AbortController()
        const signal = controller.signal
        const timeout = setTimeout(() => controller.abort(), 120000)

        try {
            const displayedRegion = params.displayRegion
            const response = await axiosForAPI.get('/ladder/', {
                params: {
                    platform: DISPLAY_TO_PLATFORM[displayedRegion],
                    region: DISPLAY_TO_REGION[displayedRegion],
                    ladderType: params.ladderType,
                    page: params.pageNumber,
                },
                signal,
            })

            clearTimeout(timeout)
            setRankedLadderDetails(response.data)
            globallyUpdateDisplayedRegion(DISPLAY_TO_PLATFORM[displayedRegion])
            setHasLoadedOnce(true)
            setLoading(false)
            setError(false)
        } catch (requestError) {
            clearTimeout(timeout)
            setHasLoadedOnce(true)
            setLoading(false)

            if (axios.isCancel(requestError)) {
                setError({ message: 'Request took excessively long. Please try again momentarily', status_code: 408 })
            } else if (requestError.response) {
                if (requestError.response.status === 404) {
                    setRankedLadderDetails({})
                } else {
                    const responseData = requestError.response.data
                    const normalizedError = responseData && typeof responseData === 'object'
                        ? responseData
                        : { message: responseData || 'Unexpected ladder error.' }
                    const statusCode = requestError.response.status
                    const derivedErrorKind = normalizedError.error_kind || (
                        [502, 503, 504].includes(statusCode)
                            ? 'transient_upstream'
                            : statusCode === 500
                                ? 'app_error'
                                : statusCode === 429
                                    ? 'rate_limit'
                                    : ''
                    )

                    setError({
                        ...normalizedError,
                        error_kind: derivedErrorKind,
                        status_code: statusCode,
                    })
                }
            } else {
                const transientNetworkCodes = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ECONNREFUSED', 'ENOTFOUND', 'ETIMEDOUT'])
                setError({
                    message: 'We could not reach the ladder service right now. Please try again in a moment.',
                    error_kind: transientNetworkCodes.has(requestError.code) ? 'transient_network' : 'app_error',
                    status_code: requestError.code || 'UNKNOWN',
                })
            }
        }
    }, [globallyUpdateDisplayedRegion, params.displayRegion, params.ladderType, params.pageNumber])

    useEffect(() => {
        setLoading(true)
        getLeaderboard()
    }, [getLeaderboard, location.pathname])

    function formatLastUpdateTime(timestamp) {
        const currentTime = new Date()
        const targetTime = new Date(timestamp)
        if (!timestamp || Number.isNaN(targetTime.getTime())) return 'Unknown'

        const seconds = Math.floor((currentTime - targetTime) / 1000)
        const minute = 60
        const hour = minute * 60
        const day = hour * 24
        const month = day * 30
        const year = day * 365

        if (seconds < minute) return 'A Moment Ago'
        if (seconds < hour) {
            const minutes = Math.floor(seconds / minute)
            return `${minutes} Minute${minutes !== 1 ? 's' : ''} Ago`
        }
        if (seconds < day) {
            const hours = Math.floor(seconds / hour)
            return `${hours} Hour${hours !== 1 ? 's' : ''} Ago`
        }
        if (seconds < month) {
            const days = Math.floor(seconds / day)
            return `${days} Day${days !== 1 ? 's' : ''} Ago`
        }
        if (seconds < year) {
            const months = Math.floor(seconds / month)
            return `${months} Month${months !== 1 ? 's' : ''} Ago`
        }
        const years = Math.floor(seconds / year)
        return `${years} Year${years !== 1 ? 's' : ''} Ago`
    }

    function getPlayerCompetitive(player) {
        return player?.competitive?.[params.ladderType] || {}
    }

    function getTierIconSource(tier) {
        const iconName = TIER_ICON_MAP[tier] || 'Rank=Unranked'
        return `${process.env.PUBLIC_URL}/assets/ranked_icons/${iconName}.png`
    }

    function formatTierLabel(player, abbreviated = false) {
        const competitive = getPlayerCompetitive(player)

        if (!competitive?.tier) {
            return 'UNRANKED'
        }

        if (APEX_TIERS.has(competitive.tier)) {
            return abbreviated ? (TIER_LABEL_ABBREVIATIONS[competitive.tier] || competitive.tier) : competitive.tier
        }

        const tierLabel = abbreviated ? (TIER_LABEL_ABBREVIATIONS[competitive.tier] || competitive.tier) : competitive.tier
        return `${tierLabel} ${competitive.division || ''}`.trim()
    }

    function getWinRate(player) {
        const wins = player?.wins || 0
        const losses = player?.losses || 0
        const totalGames = wins + losses

        if (!totalGames) {
            return 0
        }

        return Math.round((wins / totalGames) * 100)
    }

    function setLadderRegion(displayRegion) {
        if (!isPlatformSupported(DISPLAY_TO_PLATFORM[displayRegion])) {
            return
        }
        navigate(`/ladder/${displayRegion}/${params.ladderType}/1`)
    }

    function getRegionButtonClassName(isActive) {
        return `${isActive ? filterPillActiveClassName : filterPillInactiveClassName} w-full`
    }

    function renderRegionButton(regionValue) {
        const option = REGION_OPTIONS.find((regionOption) => regionOption.value === regionValue)

        if (!option) {
            return null
        }

        return (
            <button
                key={option.value}
                type="button"
                className={`${getRegionButtonClassName(params.displayRegion === option.value)} disabled:cursor-not-allowed disabled:opacity-35`}
                disabled={!isPlatformSupported(DISPLAY_TO_PLATFORM[option.value])}
                title={!isPlatformSupported(DISPLAY_TO_PLATFORM[option.value]) ? 'Unavailable during NA beta' : undefined}
                onClick={() => setLadderRegion(option.value)}
            >
                {option.label}
            </button>
        )
    }

    function renderCompactRegionButtons() {
        return (
            <div className="mt-2 grid grid-cols-4 gap-1">
                {REGION_OPTIONS.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        className={`inline-flex h-7 items-center justify-center rounded-md px-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${buttonFocusRingClass} ${
                            params.displayRegion === option.value ? ladderSelectedButtonClassName : neutralButtonOnZincSurfaceClass
                        }`}
                        disabled={!isPlatformSupported(DISPLAY_TO_PLATFORM[option.value])}
                        title={!isPlatformSupported(DISPLAY_TO_PLATFORM[option.value]) ? 'Unavailable during NA beta' : undefined}
                        onClick={() => setLadderRegion(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
        )
    }

    function setLadderType(ladderType) {
        navigate(`/ladder/${params.displayRegion}/${ladderType}/1`)
    }

    function goToPage(pageNumber) {
        navigate(`/ladder/${params.displayRegion}/${params.ladderType}/${pageNumber}`)
    }

    function renderPageButtons() {
        const currentPage = parseInt(params.pageNumber, 10)
        const pageButtons = []

        for (let page = Math.max(1, currentPage - 1); page <= Math.min(25, currentPage + 3); page += 1) {
            pageButtons.push(page)
        }

        return (
            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                {currentPage > 1 ? (
                    <button className={paginationButtonInactiveClassName} onClick={() => goToPage(1)} type="button">
                        <RiArrowLeftDoubleFill className="h-4 w-4" />
                    </button>
                ) : null}
                {pageButtons.map((page) => (
                    <button
                        key={page}
                        className={page === currentPage ? paginationButtonActiveClassName : paginationButtonInactiveClassName}
                        onClick={() => goToPage(page)}
                        type="button"
                    >
                        {page}
                    </button>
                ))}
                {currentPage < 25 ? (
                    <button className={paginationButtonInactiveClassName} onClick={() => goToPage(25)} type="button">
                        <RiArrowRightDoubleFill className="h-4 w-4" />
                    </button>
                ) : null}
            </div>
        )
    }

    const shouldShowInitialLoading = loading && !hasLoadedOnce && !error

    if (shouldShowInitialLoading) {
        return <Loading />
    }

    if (error) {
        return <LadderError error={error} />
    }

    const regionLabel = (params.displayRegion || 'na').toUpperCase()
    const ladderTypeLabel = LADDER_TYPE_META_LABELS[params.ladderType] || 'Ranked'
    const pageNumber = params.pageNumber || '1'
    const seoTitle = `${regionLabel} ${ladderTypeLabel} Ladder | High-Elo & Challenger Players | WINRATES.GG™`
    const seoDescription = `Browse high-elo League of Legends players, Challenger rankings, LP, champions, and win rates on the ${regionLabel} ${ladderTypeLabel.toLowerCase()} ranked ladder on WINRATES.GG™. Page ${pageNumber}.`
    const ladderGridTemplate = isCompactLadder ? LADDER_GRID_TEMPLATE_COMPACT : LADDER_GRID_TEMPLATE

    return (
        <div className="mx-auto w-full max-w-screen-lg px-3 pt-6 pb-4 sm:px-4 sm:pt-8 lg:px-0">
            <Seo
                title={seoTitle}
                description={seoDescription}
                canonical={`/ladder/${params.displayRegion}/${params.ladderType}/${pageNumber}`}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'CollectionPage',
                    name: seoTitle,
                    url: `https://winrates.gg/ladder/${params.displayRegion}/${params.ladderType}/${pageNumber}`,
                    description: seoDescription,
                }}
            />
            <header className="mb-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-400">Ranked ladders</div>
                <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-100 sm:text-3xl">{regionLabel} {ladderTypeLabel} Ladder</h1>
            </header>
            <section className={`grid gap-3 ${isCompactLadder ? 'grid-cols-[minmax(0,1fr)_112px] items-stretch' : 'items-stretch lg:grid-cols-[220px_minmax(0,1fr)_148px]'}`}>
                <div className={`${controlCardClassName} ${isCompactLadder ? 'col-span-2 h-auto min-h-[108px]' : 'h-[138px]'} flex flex-col`}>
                    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-300">Queue</div>
                    <div className={`mt-2 ${isCompactLadder ? 'flex flex-wrap gap-2' : 'flex flex-col items-start gap-2'}`}>
                        {LADDER_TYPE_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className={params.ladderType === option.value ? queueFilterButtonActiveClassName : queueFilterButtonInactiveClassName}
                                onClick={() => setLadderType(option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <div className={`${isCompactLadder ? 'mt-auto' : 'mt-2'} border-t border-zinc-800/70 pt-1.5 text-[10px] font-medium text-zinc-400`}>
                        Updated {loading ? 'Updating…' : formatLastUpdateTime(rankedLadderDetails?.updated_at)}
                    </div>
                </div>

                    <div className={`${controlCardClassName} ${isCompactLadder ? 'h-auto' : 'h-[138px]'}`}>
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-300">Region</div>
                        {isCompactLadder ? (
                            renderCompactRegionButtons()
                        ) : (
                            <div className="mt-2 flex items-start justify-start gap-2">
                                <div className="grid w-[52px] gap-2">
                                    {REGION_LEFT_COLUMN.map((regionValue) => renderRegionButton(regionValue))}
                                </div>

                                <div className="w-[296px] space-y-2">
                                    <div className="grid grid-cols-4 gap-2">
                                        {REGION_MIDDLE_TOP_ROW.map((regionValue) => renderRegionButton(regionValue))}
                                    </div>
                                    <div className="grid grid-cols-5 gap-2">
                                        {REGION_MIDDLE_BOTTOM_ROW.map((regionValue) => renderRegionButton(regionValue))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    {REGION_RIGHT_COLUMNS.map((column, columnIndex) => (
                                        <div key={`region-column-${columnIndex}`} className="grid w-[52px] gap-2">
                                            {column.map((regionValue) => renderRegionButton(regionValue))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={`${controlCardClassName} ${isCompactLadder ? 'h-auto min-h-[100px]' : 'h-[138px]'} flex items-center justify-center`}>
                        <LadderGlobe selectedRegion={params.displayRegion} compact={isCompactLadder} />
                    </div>
            </section>

            <section className="mt-4 w-full overflow-hidden rounded-lg border border-[#33333a] bg-zinc-950 shadow-[0_12px_28px_-24px_rgba(0,0,0,0.9)]">
                <div className="overflow-x-auto">
                    <div className={`${isCompactLadder ? 'min-w-[564px]' : 'min-w-[736px]'}`}>
                        <div
                            className={`grid items-center bg-zinc-900/65 px-3 py-3 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 ${isCompactLadder ? 'gap-x-2' : 'gap-x-2.5'}`}
                            style={{ gridTemplateColumns: ladderGridTemplate }}
                        >
                            <div className="min-w-0 whitespace-nowrap">Rank</div>
                            <div className="min-w-0 whitespace-nowrap">Summoner</div>
                            <div className="min-w-0 whitespace-nowrap">Tier</div>
                            <div className="min-w-0 whitespace-nowrap">{isCompactLadder ? 'Champs' : 'Champions'}</div>
                            <div className="min-w-0 whitespace-nowrap">{isCompactLadder ? 'WR' : 'Winrate'}</div>
                            <div className="min-w-0 whitespace-nowrap text-center">Level</div>
                        </div>

                        <div className="mt-0 space-y-0 pb-3">
                            {rankedLadderDetails?.ladder?.length ? rankedLadderDetails.ladder.map((player) => {
                                const rank = player?.calculated_rank
                                const wins = player?.wins || 0
                                const losses = player?.losses || 0
                                const winRate = getWinRate(player)
                                const totalGames = wins + losses
                                const topChampions = player?.champion_mastery?.slice(0, isCompactLadder ? 2 : 3) || []
                                const competitive = getPlayerCompetitive(player)
                                const isTopRank = rank === 1

                                return (
                                    <article
                                        key={`${player?.game_name}-${player?.tag_line}-${rank}`}
                                        className={`border-b border-zinc-800/80 px-3 py-2.5 transition-colors last:border-b-0 ${
                                            isTopRank
                                                ? 'bg-cyan-900/25 shadow-[inset_0_1px_0_rgba(103,232,249,0.3),inset_0_-1px_0_rgba(34,211,238,0.18)]'
                                                : 'bg-zinc-950 hover:bg-zinc-900'
                                        }`}
                                    >
                                        <div
                                            className={`grid items-center ${isCompactLadder ? 'gap-x-2' : 'gap-x-2.5'}`}
                                            style={{ gridTemplateColumns: ladderGridTemplate }}
                                        >
                                            <div className={`text-[15px] font-bold tracking-tight ${isTopRank ? 'text-cyan-100' : 'text-zinc-100'}`}>
                                                {rank}.
                                            </div>

                                            <div className="min-w-0">
                                                <Link
                                                    className={`block truncate text-[12px] font-semibold transition-colors hover:text-cyan-100 ${isTopRank ? 'text-cyan-50' : 'text-zinc-100'}`}
                                                    to={`/summoners/${params.displayRegion}/${player?.game_name}-${player?.tag_line}`}
                                                >
                                                    {player?.game_name}
                                                    <span className="text-zinc-300">#{player?.tag_line}</span>
                                                </Link>
                                            </div>

                                            <div className="flex items-center gap-1.5">
                                                <img
                                                    className={isCompactLadder ? 'h-6 w-6 object-contain' : 'h-7 w-7 object-contain'}
                                                    width={isCompactLadder ? '24' : '28'}
                                                    height={isCompactLadder ? '24' : '28'}
                                                    alt={`${competitive?.tier || 'Unranked'} tier emblem`}
                                                    src={getTierIconSource(competitive?.tier)}
                                                />
                                                <div className="min-w-0">
                                                    <div className="truncate text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-200">
                                                        {formatTierLabel(player, isCompactLadder)}
                                                    </div>
                                                    <div className={`${isCompactLadder ? 'text-[10px]' : 'text-[11px]'} text-zinc-300`}>
                                                        {competitive?.leaguePoints ?? 0} LP
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-1.5">
                                                {topChampions.map((champion, index) => (
                                                    <div
                                                        key={`${player?.game_name}-${champion?.nameId}-${index}`}
                                                        className={`${isCompactLadder ? 'h-7 w-7' : 'h-8 w-8'} shrink-0 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900`}
                                                    >
                                                        <LadderChampionIconWithFallback
                                                            alt={`${champion?.nameId || 'Champion'} icon`}
                                                            format="h-full w-full object-cover scale-[1.15]"
                                                            src={assetUrl(`/champion_icons/${champion?.nameId}.png`)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="min-w-0 pr-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-5 min-w-0 flex-1 overflow-hidden rounded-md border border-zinc-800 bg-zinc-950/94">
                                                        {totalGames ? (
                                                            <div className="flex h-full">
                                                                <div
                                                                    className="flex items-center justify-center bg-cyan-900/65 px-1.5 text-[9px] font-semibold text-cyan-50"
                                                                    style={{ width: `${(wins / totalGames) * 100}%` }}
                                                                >
                                                                    {wins}W
                                                                </div>
                                                                <div
                                                                    className="flex items-center justify-center bg-rose-950/55 px-1.5 text-[9px] font-semibold text-rose-100"
                                                                    style={{ width: `${(losses / totalGames) * 100}%` }}
                                                                >
                                                                    {losses}L
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="flex h-full items-center justify-center text-[10px] font-semibold text-zinc-300">
                                                                0W
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="w-10 shrink-0 text-[11px] font-medium text-zinc-300">
                                                        {winRate}%
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={`${isCompactLadder ? 'justify-self-center px-1' : ''} text-center text-sm font-semibold text-zinc-300`}>
                                                {player?.summonerLevel}
                                            </div>
                                </div>
                            </article>
                                )
                            }) : (
                                <div className="flex h-72 items-center justify-center rounded-lg border border-[#33333a] bg-zinc-950 text-sm italic text-zinc-300">
                                    Batch job for this page has not run yet. Try viewing later.
                                </div>
                            )}
                        </div>
                        </div>
                    </div>
                <div className="border-t border-zinc-800/70 px-3 py-3 sm:px-4">
                    {renderPageButtons()}
                </div>
            </section>
        </div>
    )
}

export default Ladder
